"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import getIndianCurrencyFormat from "@helpers/getIndianCurrencyFormat";
import getMonthlyEMI from "@helpers/getMonthlyEMI";
import styles from "./CarFinance.module.scss";
import AckoDriveLogo from "../../atoms/AckoDriveLogo";
import { Modal } from "@components/atoms/Modal";
import Button from "@components/atoms/Button";
import { getLoginUrl } from "@utils/getLoginUrl";
import assetLoader from "@utils/internalImageLoder";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";

export const CarFinance = ({
  isLoggedIn,
  savingsBreakupData,
  onRoadPrice,
  // ADPrice,
  carFinanceValuePropositions = [],
  carFinanceData,
  carFinanceBankData = [],
  selectedBankValue,
  maxSavings,
  brandName,
  modelName,
  variantName,
  pageType,
  isSellable,
  cityName,
  carAvailability,
}: {
  isLoggedIn?: boolean;
  savingsBreakupData: {
    ackodiscount: number;
    newyear_discount?: number;
    [key: string]: any;
  };
  onRoadPrice: number;
  ADPrice: number;
  carFinanceData: any;
  carFinanceValuePropositions?: string[];
  carFinanceBankData?: any[];
  selectedBankValue: any;
  maxSavings: any;
  brandName: string;
  modelName: string;
  variantName: string;
  cityName: string;
  isSellable: boolean;
  pageType: string;
  carAvailability?: string;
}) => {
  // State for modal and editable fields
  const initialDuration = 7;
  const getRIPPlusConsumer = (savingsBreakup: { [key: string]: number }) => {
    return savingsBreakup?.ripdiscount + savingsBreakup?.consumerdiscount || 0;
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const defaultDownPayment =
    (Number(onRoadPrice || 0) -
      getRIPPlusConsumer(savingsBreakupData) -
      Number(savingsBreakupData?.ackodiscount || 0) -
      Number(savingsBreakupData?.newyear_discount || 0)) *
    0.2;
  const [downPayment, setDownPayment] = useState(defaultDownPayment);
  const [loanTenure, setLoanTenure] = useState(initialDuration);
  const bankArray = carFinanceBankData || [];
  // Find the bank whose interest_rate matches selectedBankInterestDataValue (as string or number)
  const initialSelectedBank =
    bankArray.find(
      (bank) =>
        parseFloat(bank.interest_rate) === parseFloat(selectedBankValue?.id)
    ) ||
    bankArray[0] ||
    null;
  const [selectedBank, setSelectedBank] = useState(initialSelectedBank);
  const parseInterest = (val: any) => {
    const num = parseFloat(val);
    return isNaN(num) ? "" : num;
  };
  const [otherInterest, setOtherInterest] = useState(
    parseInterest(initialSelectedBank?.interest_rate)
  );
  const [carLoanDiscount, setCarLoanDiscount] = useState(0);
  const [iteration, setIteration] = useState(0);
  const [isInitial, setIsInitial] = useState(true);
  const [checkChangeDownpayment, setCheckChangeDownpayment] =
    useState<boolean>(false);
  const financeSlabs =
    carFinanceData?.car_finance?.ackodrive_finance_saving_slab?.sort(
      (a: { max_loan_amount: number }, b: { max_loan_amount: number }) => {
        return a.max_loan_amount - b.max_loan_amount;
      }
    );

  const getCarLoanDiscount = (financeSlabData: any[]) => {
    const val =
      Number(onRoadPrice || 0) -
      getRIPPlusConsumer(savingsBreakupData) -
      carLoanDiscount -
      savingsBreakupData?.ackodiscount -
      Number(downPayment);

    const discountValueSlab = financeSlabData?.find((financeSlab) => {
      return financeSlab.max_loan_amount >= val;
    });
    if (!discountValueSlab) {
      return Number(maxSavings) || 0;
    }
    return discountValueSlab.saving_amount;
  };

  const ADPrice =
    Number(onRoadPrice || 0) -
    getRIPPlusConsumer(savingsBreakupData) -
    carLoanDiscount -
    savingsBreakupData?.ackodiscount -
    Number(savingsBreakupData?.newyear_discount || 0);

  useEffect(() => {
    if (iteration < 1 && downPayment && financeSlabs) {
      setCarLoanDiscount(getCarLoanDiscount(financeSlabs));
      setIteration(iteration + 1);
      if (isInitial && iteration === 0) {
        setIsInitial(false);
      }
    }
  }, [downPayment, financeSlabs]);

  useEffect(() => {
    const minValue = getIndianCurrencyFormat(
      (Number(onRoadPrice || 0) -
        getRIPPlusConsumer(savingsBreakupData) -
        carLoanDiscount -
        savingsBreakupData?.ackodiscount -
        Number(savingsBreakupData?.newyear_discount || 0)) *
        0.1
    );
    const currentValue =
      (Number(onRoadPrice || 0) -
        getRIPPlusConsumer(savingsBreakupData) -
        carLoanDiscount -
        savingsBreakupData?.ackodiscount -
        Number(savingsBreakupData?.newyear_discount || 0)) *
      0.2;
    if (!checkChangeDownpayment) {
      setDownPayment(currentValue);
      setDownPaymentInputValue(getIndianCurrencyFormat(currentValue));
    } else {
      if (Number(downPayment) < convertAmountToNumber(minValue)) {
        setDownPayment(currentValue);
        setDownPaymentInputValue(getIndianCurrencyFormat(currentValue));
      }
    }
  }, [carLoanDiscount]);

  // Edit mode state: 'downpayment' | 'tenure' | 'interest' | undefined
  const [editMode, setEditMode] = useState<
    "downpayment" | "tenure" | "interest" | undefined
  >(undefined);

  // Open modal for any edit
  const handleEditClick = (mode: "downpayment" | "tenure" | "interest") => {
    setDownPayment(downPayment);
    setLoanTenure(loanTenure);
    setOtherInterest(otherInterest);
    setSelectedBank(selectedBank);
    setEditMode(mode);
    setIsModalOpen(true);
  };

  const handleBankChange = (bank: any) => {
    setSelectedBank(bank);
    setOtherInterest(bank?.interest_rate);
  };
  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditMode(undefined);
  };

  // Handle compare (submit)
  const handleCompare = () => {
    setIsModalOpen(false);
    setEditMode(undefined);
    // Optionally, call a prop or update parent state here
  };

  // Helper to convert formatted string to number
  const convertAmountToNumber = (val: string) =>
    Number(val.replace(/[^\d]/g, ""));

  // Calculate min/max down payment
  const carValue =
    Number(onRoadPrice || 0) -
    (savingsBreakupData?.ackodiscount || 0) -
    Number(savingsBreakupData?.newyear_discount || 0);
  const minDownPayment = carValue * 0.1;
  const maxDownPayment = carValue * 0.8;

  // State for formatted input and error
  const [downPaymentInputValue, setDownPaymentInputValue] = useState(
    getIndianCurrencyFormat(defaultDownPayment)
  );
  const [downPaymentError, setDownPaymentError] = useState("");

  // State for interest input and error
  const [interestInputValue, setInterestInputValue] = useState(
    otherInterest !== "" ? String(otherInterest) : ""
  );
  const [interestError, setInterestError] = useState("");

  // Sync interest input value with otherInterest when modal opens or selected bank changes
  useEffect(() => {
    setInterestInputValue(otherInterest !== "" ? String(otherInterest) : "");
    setInterestError("");
  }, [isModalOpen, selectedBank]);

  // For dynamic slider background
  const min = 2;
  const max = 7;
  const percent = ((loanTenure - min) / (max - min)) * 100;

  const getTotalEmiSavings = () => {
    const dealerMonthlyEMI = convertAmountToNumber(
      getMonthlyEMI(
        onRoadPrice - getRIPPlusConsumer(savingsBreakupData) - downPayment,
        Number(otherInterest),
        loanTenure
      )
    );
    const adMonthlyEMI = convertAmountToNumber(
      getMonthlyEMI(
        ADPrice - downPayment,
        Number(initialSelectedBank?.interest_rate),
        loanTenure
      )
    );
    const emiSavings = (dealerMonthlyEMI - adMonthlyEMI) * loanTenure * 12;
    if (emiSavings < 0) {
      return "-";
    }
    return `₹${getIndianCurrencyFormat(Math.abs(emiSavings))}`;
  };

  const totalAckoDriveSavings = getIndianCurrencyFormat(
    savingsBreakupData?.ackodiscount +
      Number(savingsBreakupData?.newyear_discount || 0) +
      carLoanDiscount +
      getTotalEmiSavings()
  );
  const sliderBg = `linear-gradient(to right, rgb(140, 233, 154) ${percent}%, rgb(240, 240, 246) ${percent}%)`;

  return (
    <div className={styles.carFinanceWrapper}>
      {isLoggedIn && (
        <>
          {/* Modal for editing down payment or loan tenure */}
          <Modal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            title="Customise your loan"
            className={styles.emiModal}
          >
            <div className={styles.modalContentWrapper}>
              {/* Downpayment input */}
              <div
                className={`${styles.inputWrapper} ${
                  editMode === "downpayment" ? "" : styles.isMobileHidden
                }`}
              >
                <label className={styles.label} htmlFor="downPaymentInput">
                  Down Payment
                </label>
                <span className={styles.rupeePrefix}>₹</span>
                <input
                  id="downPaymentInput"
                  type="text"
                  value={downPaymentInputValue}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const num = convertAmountToNumber(raw);
                    setDownPaymentInputValue(getIndianCurrencyFormat(num));
                    setDownPayment(num);
                    if (num < minDownPayment) {
                      setDownPaymentError(
                        "Minimum down payment can be 10% of the car value"
                      );
                    } else if (num > maxDownPayment) {
                      setDownPaymentError(
                        "Maximum down payment can be 80% of the car value"
                      );
                    } else {
                      setDownPaymentError("");
                    }
                  }}
                  className={`${styles.inputField} ${
                    downPaymentError ? styles.inputError : styles.inputNormal
                  }`}
                  min={0}
                />
                {downPaymentError && (
                  <div className={styles.errorText}>{downPaymentError}</div>
                )}
              </div>
              {/* Loan Tenure slider */}
              <div
                className={`${styles.sliderWrapper} ${
                  editMode === "tenure" ? "" : styles.isMobileHidden
                }`}
              >
                <div className={styles.loanTenureHeading}>Loan Tenure</div>
                <input
                  type="range"
                  min={min}
                  max={max}
                  className={styles.customRange}
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(Number(e.target.value))}
                  style={{
                    appearance: "none",
                    width: "100%",
                    height: "6px",
                    outline: "none",
                    borderRadius: "4px",
                    margin: "9px 0px",
                    background: sliderBg,
                  }}
                />
                <div className={styles.ticksWrapper}>
                  {[2, 3, 4, 5, 6, 7].map((y) => (
                    <div
                      key={y}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minWidth: 0,
                      }}
                    >
                      <span className={styles.tick} />
                      <span className={styles.tickLabel}>{y}y</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Partnered banks */}
              <div
                className={`${styles.inputWrapper} ${
                  editMode === "interest" ? "" : styles.isMobileHidden
                }`}
              >
                <div className={styles.partneredBanksHeading}>
                  ACKO Drive partnered banks
                </div>
                <div className={styles.banksRow}>
                  {bankArray.map((bank) => {
                    const isSelected = selectedBank?.id === bank.id;
                    return (
                      <div
                        key={bank.id}
                        className={`${styles.bankCard}${
                          isSelected ? " " + styles.bankCardSelected : ""
                        }`}
                        onClick={() => handleBankChange(bank)}
                      >
                        {bank.logo_image && (
                          <Image
                            src={bank.logo_image}
                            alt={bank.provider_name}
                            width={30}
                            height={30}
                            className={styles.bankLogo}
                            loading="lazy"
                          />
                        )}
                        <div className={styles.bankName}>
                          {bank.provider_name}
                        </div>
                        <div className={styles.bankInterest}>
                          {parseFloat(bank.interest_rate)}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Other dealer interest */}
              <div
                className={`${styles.inputWrapper} ${
                  editMode === "interest" ? "" : styles.isMobileHidden
                }`}
              >
                <label className={styles.label} htmlFor="otherInterestInput">
                  Other dealer interest (optional)
                </label>
                <span className={styles.rupeePrefix}>{"%"}</span>
                <input
                  id="otherInterestInput"
                  type="text"
                  value={interestInputValue}
                  placeholder="Other dealer interest (optional)"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                      setInterestInputValue("");
                      setInterestError(
                        "Minimum interest rate possible is 6.4%"
                      );
                      setOtherInterest("");
                    } else {
                      if (
                        Number(val) >= 0 &&
                        Number(val) <= 100 &&
                        /^\d*[0-9]\d*$/.test(val.replaceAll(".", "")) &&
                        /^\d+(\.\d{0,2})?$/.test(val)
                      ) {
                        setInterestInputValue(val);
                        setOtherInterest(Number(val));
                        if (val.endsWith(".")) {
                          setInterestError("Put 0 after .");
                        } else if (Number(val) < 6.4) {
                          setInterestError(
                            "Minimum interest rate possible is 6.4%"
                          );
                        } else if (Number(val) > 20) {
                          setInterestError(
                            "Maximum interest rate possible is 20%"
                          );
                        } else {
                          setInterestError("");
                        }
                      }
                    }
                  }}
                  className={`${styles.inputField} ${
                    interestError ? styles.inputError : styles.inputNormal
                  }`}
                  min={0}
                  step="0.01"
                />
                {interestError && (
                  <div className={styles.errorText}>{interestError}</div>
                )}
              </div>
              <Button className={styles.compareButton} onClick={handleCompare}>
                Compare
              </Button>
            </div>
          </Modal>
        </>
      )}
      {/* Benefits Card */}
      <div className={styles.benefitsCard}>
        {carFinanceValuePropositions.map(
          (carFinanceValueProposition: string, idx: number) => (
            <div className={styles.benefitRow} key={idx}>
              <Image
                src={`${assetLoader()}/icons/tick_icon.svg`}
                alt="tick"
                width={20}
                height={20}
                loading="lazy"
              />
              <span className={styles.benefitText}>
                {carFinanceValueProposition}
              </span>
            </div>
          )
        )}
      </div>
      {isLoggedIn && (
        <>
          {/* Savings Banner & Table Card */}
          <div className={styles.savingsTableCard}>
            <div className={styles.savingsBanner}>
              <Image
                src={`${assetLoader()}/icons/discount_icon.svg`}
                alt="discount"
                width={24}
                height={24}
                loading="lazy"
              />
              <span className={styles.savingsBannerText}>
                You&apos;ll save{" "}
                <span className={styles.savingsAmount}>
                  {`₹${totalAckoDriveSavings}`}
                </span>{" "}
                with ACKO Drive
              </span>
            </div>
            <table className={styles.emiTable}>
              <tr className={styles.emiTableR}>
                <th className={styles.emiTableEmptyTH} />
                <th className={styles.emiTableTHDealer}>Other Dealer</th>
                <th className={`${styles.emiTableTH} ${styles.logoTh}`}>
                  <AckoDriveLogo className={styles.logoWrapper} />
                </th>
              </tr>
              <tr className={styles.emiTableR}>
                <td className={styles.emiTableText}>On road Price</td>
                <td className={styles.emiTableDealerData}>
                  {`₹${getIndianCurrencyFormat(
                    isLoggedIn
                      ? Number(onRoadPrice) -
                          getRIPPlusConsumer(savingsBreakupData)
                      : onRoadPrice
                  )}`}
                </td>
                <td className={styles.emiTableADData}>
                  {`₹${getIndianCurrencyFormat(
                    isLoggedIn
                      ? Number(onRoadPrice) -
                          getRIPPlusConsumer(savingsBreakupData)
                      : onRoadPrice
                  )}`}
                </td>
              </tr>
              <tr className={styles.emiTableR}>
                <td className={styles.emiTableText}>
                  Discount from ACKO Drive
                </td>
                <td className={styles.emiTableDealerData}>-</td>
                <td className={`${styles.emiTableADData} ${styles.isSavings}`}>
                  {`₹${getIndianCurrencyFormat(
                    savingsBreakupData?.ackodiscount +
                      Number(savingsBreakupData?.newyear_discount || 0)
                  )}`}
                </td>
              </tr>
              <tr className={styles.emiTableR}>
                <td className={styles.emiTableText}>Car loan discount</td>
                <td className={styles.emiTableDealerData}>-</td>
                <td className={`${styles.emiTableADData} ${styles.isSavings}`}>
                  {`₹${getIndianCurrencyFormat(carLoanDiscount)}`}
                </td>
              </tr>
              <tr className={styles.emiTableR}>
                <td
                  className={`${styles.emiTableText} ${styles.isBold} ${styles.hasBorder}`}
                >
                  Car Price
                </td>
                <td
                  className={`${styles.emiTableDealerData} ${styles.isBold} ${styles.hasBorder}`}
                >
                  {`₹${getIndianCurrencyFormat(
                    Number(onRoadPrice || 0) -
                      getRIPPlusConsumer(savingsBreakupData)
                  )}`}
                </td>
                <td
                  className={`${styles.emiTableADData} ${styles.isBold} ${styles.hasBorder}`}
                >
                  {`₹${getIndianCurrencyFormat(ADPrice)}`}
                </td>
              </tr>
              <tr className={styles.emiTableR}>
                <td
                  className={`${styles.emiTableText} ${styles.isEditable}`}
                  onClick={() => handleEditClick("downpayment")}
                >
                  Down Payment
                  <Image
                    src={`${assetLoader()}/icons/Edit_Pencil.svg`}
                    alt="Edit"
                    width={16}
                    height={16}
                  />
                </td>
                <td className={styles.emiTableDealerData}>
                  {`₹${getIndianCurrencyFormat(downPayment)}`}
                </td>
                <td className={`${styles.emiTableADData}`}>
                  {`₹${getIndianCurrencyFormat(downPayment)}`}
                </td>
              </tr>
              <tr className={styles.emiTableR}>
                <td className={styles.emiTableText}>Loan Amount</td>
                <td className={styles.emiTableDealerData}>
                  {`₹${getIndianCurrencyFormat(
                    Number(onRoadPrice) -
                      getRIPPlusConsumer(savingsBreakupData) -
                      Number(downPayment)
                  )}`}
                </td>
                <td className={styles.emiTableADData}>
                  {`₹${getIndianCurrencyFormat(
                    Number(ADPrice) - Number(downPayment)
                  )}`}
                </td>
              </tr>
              <tr className={styles.emiTableR}>
                <td
                  className={`${styles.emiTableText} ${styles.isEditable}`}
                  onClick={() => handleEditClick("interest")}
                >
                  Interest
                  <Image
                    src={`${assetLoader()}/icons/Edit_Pencil.svg`}
                    alt="Edit"
                    width={16}
                    height={16}
                  />
                </td>
                <td
                  className={`${styles.emiTableDealerData} ${styles.isSemiBold}`}
                >
                  {otherInterest ? otherInterest + "%" : "-"}
                </td>
                <td
                  className={`${styles.emiInterestTableADData} ${styles.isSemiBold}`}
                >
                  {`${initialSelectedBank?.interest_rate}%`}
                </td>
              </tr>
              <tr className={styles.emiTableR}>
                <td
                  className={`${styles.emiTableText} ${styles.isEditable}`}
                  onClick={() => handleEditClick("tenure")}
                >
                  Duration
                  <Image
                    src={`${assetLoader()}/icons/Edit_Pencil.svg`}
                    alt="Edit"
                    width={16}
                    height={16}
                  />
                </td>
                <td className={styles.emiTableDealerData}>
                  {`${loanTenure} years`}
                </td>
                <td className={`${styles.emiTableADData}`}>
                  {`${loanTenure} years`}
                </td>
              </tr>
              {/* Monthly EMI Row */}
              <tr className={styles.emiTableR}>
                <td
                  className={
                    styles.emiTableText +
                    " " +
                    styles.isBold +
                    " " +
                    styles.hasBorder
                  }
                >
                  Monthly EMI
                </td>
                <td
                  className={
                    styles.emiTableDealerData +
                    " " +
                    styles.isBold +
                    " " +
                    styles.hasBorder
                  }
                >
                  {`₹${getMonthlyEMI(
                    Number(onRoadPrice) -
                      getRIPPlusConsumer(savingsBreakupData) -
                      Number(downPayment),
                    Number(otherInterest),
                    Number(loanTenure)
                  )}`}
                </td>
                <td
                  className={
                    styles.emiTableADData +
                    " " +
                    styles.isBold +
                    " " +
                    styles.hasBorder
                  }
                >
                  {`₹${getMonthlyEMI(
                    Number(ADPrice) - Number(downPayment),
                    Number(initialSelectedBank?.interest_rate),
                    Number(loanTenure)
                  )}`}
                </td>
              </tr>
              {/* Total EMI Savings Row */}
              <tr className={styles.emiTableR}>
                <td className={styles.emiTableText}>
                  {`Total EMI Savings (${loanTenure} years)`}
                </td>
                <td className={styles.emiTableDealerData}>-</td>
                <td className={`${styles.emiTableADData} ${styles.isSavings}`}>
                  {getTotalEmiSavings()}
                </td>
              </tr>
            </table>
          </div>
        </>
      )}

      <>
        <div className={styles.partneredBanksRow}>
          <span className={styles.partneredBanksLabel}>
            Our partnered banks:
          </span>
          <span className={styles.bankIcons}>
            {bankArray
              ?.filter((bank) => bank.is_active)
              ?.map((bank, idx) => (
                <Image
                  key={bank.provider_name}
                  src={bank.logo_image}
                  alt={bank.provider_name}
                  width={20}
                  height={20}
                  className={styles.bankLogo}
                  loading="lazy"
                />
              ))}
          </span>
        </div>
        <div className={styles.buttonDisclaimerWrapper}>
          {!isLoggedIn && (
            <Button
              className={styles.financeButton}
              onClick={async () => {
                await trackEventWithCommonProps(
                  "action",
                  EventType.TRACK_EVENT,
                  {
                    pageType: pageType,
                    additionalProps: {
                      action_type: "tap_btn",
                      action_name: "tap_btn_emi",
                      car_availability: carAvailability,
                      vehicle_make: brandName,
                      vehicle_model: modelName,
                      vehicle_variant: variantName,
                      city_name: cityName,
                    },
                  }
                );
                if (typeof window !== "undefined") {
                  sessionStorage.setItem("loginType", "Car Finance");
                  localStorage.setItem(
                    "MMV",
                    JSON.stringify({
                      make: brandName,
                      model: modelName,
                      variant: variantName,
                    })
                  );
                }
                window.location.href = getLoginUrl(window.location.href);
              }}
            >
              Calculate your EMI
            </Button>
          )}

          <div className={styles.disclaimerText}>
            <strong>Disclaimer :</strong>
            {` Interest rates by banks represents
              the best possible rate, which is subjected to change as per CIBIL
              score and preventive lending history of the customer`}
          </div>
        </div>
      </>
    </div>
  );
};
